import { z } from 'zod/v4';
import { BasicBool, BasicInt, BasicString } from '../primitives';

export type EditableStatusConfig = {
  isEditable?: boolean;
  reason?: string;
  maxLength?: number;
  allowedCharacters?: string;
};

export const DEFAULT_EDITABLE_STATUS_VALUES: Required<
  Pick<EditableStatusConfig, 'maxLength' | 'allowedCharacters'>
> = {
  maxLength: 60_000,
  allowedCharacters: '[a-zA-Z0-9~`!@#$%^&*()\\-_+={\\[}\\]|;:\'",<>.\\\\\\/?\\n\\t\\r]',
};

export const EditableStatusSchema = z
  .object({
    isEditable: BasicBool.meta({
      description: 'Whether the field can be edited by the user',
      examples: [true, false],
    }),
    reason: BasicString.optional().meta({
      description: 'Reason why the field is not editable',
      examples: ['Field locked by administrator', 'Encounter already signed'],
    }),
    maxLength: BasicInt.optional().meta({
      description: 'Maximum character length allowed for editing',
      examples: [60_000, 1000, 500],
    }),
    allowedCharacters: BasicString.optional().meta({
      description: 'Regex pattern defining allowed characters for input validation',
      examples: ['[a-zA-Z0-9 ]', '[0-9-()]'],
    }),
  })
  .default({
    isEditable: true,
    maxLength: DEFAULT_EDITABLE_STATUS_VALUES.maxLength,
    allowedCharacters: DEFAULT_EDITABLE_STATUS_VALUES.allowedCharacters,
  });

export type BackwardCompatibleEditableStatus = boolean | EditableStatus;
export type EditableStatus = z.infer<typeof EditableStatusSchema>;

export const DEFAULT_EDITABLE_STATUS: Partial<EditableStatus> = DEFAULT_EDITABLE_STATUS_VALUES;

export function createEditableStatus(
  configOrIsEditable?: boolean | EditableStatusConfig,
  reason?: string,
  overrides?: Partial<Omit<EditableStatus, 'isEditable' | 'reason'>>,
): EditableStatus {
  if (typeof configOrIsEditable === 'object') {
    return EditableStatusSchema.parse(configOrIsEditable);
  }
  return EditableStatusSchema.parse({
    isEditable: configOrIsEditable !== undefined ? configOrIsEditable : true,
    ...(reason ? { reason } : {}),
    ...overrides,
  });
}

export function getAllowedCharactersRegex(pattern?: string): RegExp | undefined {
  if (!pattern) {
    return undefined;
  }
  return new RegExp(`^[${pattern}]*$`);
}
